import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Sparkles, Compass, ShieldCheck, Users, Rocket, AlertTriangle,
} from "lucide-react";
import { RiskBanner, RiskFooter } from "@/components/RiskDisclaimer";

const SECTIONS = [
  {
    id: "story",
    icon: Sparkles,
    label: "The Story Behind TradingoX",
    body: [
      "TradingoX was first envisioned by Charles Cawv Thao in 2015. What began as an idea for a smarter, more disciplined approach to trading gradually evolved over the following decade.",
      "After years of learning, refining the concept, and confronting the realities of the financial markets, Charles brought the TradingoX vision to fruition in 2026 — more than ten years after the original idea was conceived.",
    ],
  },
  {
    id: "lessons",
    icon: Compass,
    label: "Born From Experience — and Hard Lessons",
    body: [
      "Charles was a seasonal trader who understood both the excitement and uncertainty that can come with investing and trading. Over the years, he also experienced significant financial losses — including losses connected to scams and unsuccessful trading and investment decisions.",
      "Rather than allowing those experiences to end his interest in the markets, Charles used them as motivation to rethink how everyday people approach trading.",
      "One lesson became especially important to him: trading should not be driven primarily by fear, excitement, greed, impulse, or emotion. A trader needs a defined strategy, appropriate risk controls, and a disciplined process.",
      "That philosophy became part of the foundation for TradingoX: Trade smarter. Stay disciplined. Manage risk. Keep control of your funds. Follow a system — not your emotions.",
    ],
  },
  {
    id: "why",
    icon: ShieldCheck,
    label: "Why TradingoX Was Created",
    body: [
      "TradingoX was developed around the idea that technology can automate a defined trading strategy and reduce the need for constant manual decision-making. Instead of requiring a person to sit in front of a screen watching every price movement, the system is designed to monitor the market and execute trades according to predetermined rules and user-selected settings.",
      "The goal is not to eliminate market risk — no trading system can do that. Rather, TradingoX is intended to help users approach trading with greater consistency, automation, and discipline.",
      "An equally important principle is control of funds. TradingoX is designed to connect with a supported brokerage account for trade execution rather than requiring users to transfer their investment funds to TradingoX itself. The brokerage remains responsible for custody of the brokerage assets, subject to the broker's terms and account structure.",
    ],
  },
  {
    id: "for-everyone",
    icon: Users,
    label: "Designed for Experienced and Inexperienced Traders",
    body: [
      "Charles understood that sophisticated technology should not be reserved only for professional or highly experienced traders. TradingoX was therefore conceived with accessibility and simplicity in mind.",
      "For an experienced trader, automation may provide a way to apply a strategy more consistently and reduce repetitive manual work. For a newer trader, the platform is intended to provide a simpler way to understand and use a rules-based trading system — including the opportunity to begin with simulated or paper trading before deciding whether real-money trading is appropriate.",
      "The philosophy is simple: You shouldn't have to be a professional trader to use professional-style automation.",
      "TradingoX is intended to make automated trading easier to understand and operate while keeping the user responsible for important decisions such as risk tolerance, position sizing, trading preferences, and whether to trade with real money.",
    ],
  },
  {
    id: "vision",
    icon: Rocket,
    label: "More Than Ten Years in the Making",
    body: [
      "TradingoX represents more than a software platform. It is the result of an idea that began in 2015, lessons learned through both successes and losses, and a belief that trading can be approached with greater discipline through technology.",
      "The vision that ultimately emerged is straightforward:",
      "• Don't trade harder. Trade smarter.",
      "• Don't let emotions control every decision. Follow a system.",
      "• Don't surrender control of your money. Know where your funds are held.",
      "• Start carefully. Manage your risk. Let technology handle the repetitive work.",
      "TradingoX — Trade Smarter. On Autopilot.",
    ],
  },
];

export default function AboutPage() {
  const BG_IMG =
    "https://customer-assets-4nw71qhi.emergentagent.net/job_smart-buy-sell-20/artifacts/eniswbiq_CAWV%20TRADINGOX.png";
  return (
    <div className="min-h-screen bg-[#0B0C12] text-slate-100 flex flex-col relative overflow-hidden">
      {/* Founder desk background */}
      <div
        aria-hidden
        data-testid="about-bg"
        className="pointer-events-none fixed inset-0 z-0 bg-center bg-no-repeat bg-cover opacity-70"
        style={{ backgroundImage: `url("${BG_IMG}")` }}
      />
      {/* Contrast wash so text stays readable */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-0 bg-gradient-to-b from-[#0B0C12]/55 via-[#0B0C12]/70 to-[#0B0C12]/90"
      />
      <div className="relative z-10 flex flex-col min-h-screen">
      <RiskBanner />
      <header className="max-w-5xl w-full mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
        <Link to="/" data-testid="about-back-home" className="flex items-center gap-3 group">
          <img
            src="/brand/tradingox-logo.png"
            alt="TradingoX"
            className="w-10 h-10 rounded-full ring-1 ring-white/10 shadow-[0_0_20px_rgba(16,185,129,0.25)]"
          />
          <div className="font-display text-lg font-bold tracking-tight">TradingoX</div>
        </Link>
        <Link
          to="/"
          data-testid="about-back-btn"
          className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-100 transition"
        >
          <ArrowLeft className="w-4 h-4" /> Back home
        </Link>
      </header>

      <main className="max-w-3xl w-full mx-auto px-4 sm:px-6 pt-6 sm:pt-8 pb-16 flex-1">
        <div className="mb-10 text-center">
          <div className="text-[11px] font-mono uppercase tracking-[0.35em] text-emerald-300 mb-2">
            about us
          </div>
          <h1 className="font-display text-3xl sm:text-5xl font-bold tracking-tight">
            Ten years in the <span className="text-emerald-400">making.</span>
          </h1>
          <p className="mt-3 text-slate-400 text-base max-w-2xl mx-auto">
            The idea, the lessons, and the philosophy behind TradingoX — Trade Smarter. On Autopilot.
          </p>
          <p className="mt-1 text-[11px] font-mono text-slate-500">Founded by Charles Cawv Thao · Envisioned 2015 · Launched 2026</p>
        </div>

        <nav data-testid="about-toc" className="mb-10 flex flex-wrap gap-2 justify-center">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              data-testid={`about-toc-${s.id}`}
              className="inline-flex items-center gap-1.5 h-9 px-3 sm:px-4 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 hover:border-emerald-400/40 text-slate-200 hover:text-white text-[11px] sm:text-[12px] font-semibold transition"
            >
              <s.icon className="w-3.5 h-3.5 text-emerald-300" />
              {s.label}
            </a>
          ))}
        </nav>

        <div className="space-y-8 sm:space-y-10">
          {SECTIONS.map((s, idx) => (
            <section
              key={s.id}
              id={s.id}
              data-testid={`about-section-${s.id}`}
              className="rounded-2xl border border-white/10 bg-[#0F111A]/70 backdrop-blur-sm p-5 sm:p-8 scroll-mt-24"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="grid place-items-center w-10 h-10 rounded-full bg-emerald-500/15 text-emerald-300 shrink-0">
                  <s.icon className="w-5 h-5" />
                </span>
                <div className="min-w-0">
                  <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300">
                    Chapter {String(idx + 1).padStart(2, "0")}
                  </div>
                  <h2 className="font-display text-lg sm:text-2xl font-bold text-slate-100 leading-tight">
                    {s.label}
                  </h2>
                </div>
              </div>
              <div className="space-y-3 text-[14px] text-slate-300 leading-relaxed">
                {s.body.map((p, i) => (
                  <p key={`${s.id}-p-${i}`}>{p}</p>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Founder quote card */}
        <div className="mt-10 rounded-2xl border border-emerald-400/20 bg-gradient-to-br from-emerald-500/10 via-transparent to-transparent p-6 sm:p-8">
          <blockquote className="border-l-2 border-emerald-400/50 pl-4">
            <p className="text-slate-200 italic text-[15px] sm:text-base leading-relaxed">
              &ldquo;You can work harder, earn more, and save more — but true financial growth begins when
              you learn to make your money grow, little by little, every day.&rdquo;
            </p>
            <footer className="mt-3 text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-300 not-italic">
              — Charles Cawv Thao, Founder / Developer
            </footer>
          </blockquote>
        </div>

        {/* Warning / disclaimer */}
        <section
          data-testid="about-disclaimer"
          className="mt-10 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5 sm:p-6"
        >
          <div className="flex items-start gap-3">
            <span className="grid place-items-center w-9 h-9 rounded-full bg-amber-500/15 text-amber-300 shrink-0 mt-0.5">
              <AlertTriangle className="w-4 h-4" />
            </span>
            <div>
              <div className="font-display text-base sm:text-lg font-bold text-amber-100">
                Warning & Disclaimer
              </div>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                Trading and investing involve substantial risk, including the possible loss of principal.
                Automated trading does not guarantee profits or prevent losses. Past performance and
                simulated results do not guarantee future results.
              </p>
              <Link
                to="/terms"
                className="inline-block mt-3 text-xs font-mono uppercase tracking-[0.2em] text-amber-200 hover:text-white underline underline-offset-4"
              >
                Read full terms & disclaimer →
              </Link>
            </div>
          </div>
        </section>

        <div className="mt-12 rounded-2xl border border-emerald-400/20 bg-gradient-to-br from-emerald-500/10 via-transparent to-transparent p-6 text-center">
          <div className="text-sm text-slate-400 mb-1">Have a question for the founder?</div>
          <div className="font-display text-lg sm:text-xl font-bold">
            Email{" "}
            <a href="mailto:mail@charlesthao.com" className="text-emerald-300 underline underline-offset-2">
              mail@charlesthao.com
            </a>{" "}
            or{" "}
            <Link to="/contact" className="text-emerald-300 underline underline-offset-2">
              send a message
            </Link>
            .
          </div>
        </div>
      </main>

      <RiskFooter />
      </div>
    </div>
  );
}
